import React, { useState, useEffect, useContext, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { renderToString } from 'react-dom/server';
import DeckGL, { IconLayer } from 'deck.gl';
import { Map, Popup } from 'react-map-gl';
import { getInfraestructuresByLocation } from '@/services/measurement';
import { getMapsAction } from '@/redux/slices/mapSlice';
import { useParams } from 'react-router-dom';
import { interactiveSimulationsEventsService } from '@/events/interactiveSimulations';
import { useGisMapData } from '@/pages/Dashboard/hooks/useGisMapData';
import { useGisLayers, getGisTooltip } from '@/pages/Dashboard/hooks/useGisLayers';
import TourInteractiveSimulationContext from '@/contexts/SimulationsContext';
import { Button, InputNumber } from 'antd';
import '@/assets/css/content.css';
import './InteractiveSimulationMap.scss';
import dayjs from 'dayjs';
import { setSelectedLocationThunk } from '../../redux/slices/locationsSlice';
import { generateInfraImageToInteractiveSimulation } from '../../utils/mapUtils';
import { infraTypes } from '@/statics/constants/infraTypes';

const mapboxApiAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const initialStyle = {
  position: 'relative',
  width: '100%',
  height: '100%',
};

// Viewport settings
const initialViewState = {
  longitude: -122.45,
  latitude: 37.78,
  zoom: 11.5,
  pitch: 0,
  bearing: 0,
};

// const multiply = 100;
// const maxTime = 10;
// const step = 2;
// variables

const userId = localStorage.getItem('userId');

const getValidationRulesForInfraType = (infrastructureTypeId) => {
  switch (infrastructureTypeId) {
    case infraTypes.PUMP:
    case infraTypes.ORIFICE:
    case infraTypes.WEIR:
    case infraTypes.VALVE:
    case infraTypes.JUNCTION:
      return {
        min: 0,
        max: null,
        step: 0.1,
        precision: 2,
        validateValue: (value) => value >= 0
      };
    case infraTypes.PIPE:
      return {
        min: 0,
        max: 1,
        step: 1,
        precision: 0,
        validateValue: (value) => value === 0 || value === 1
      };
    default:
      return {
        min: 0,
        max: 1,
        step: 0.1,
        precision: 3,
        validateValue: (value) => true
      };
  }
};

const MapInteractiveSimulation = (props) => {
  const {
    selectedLocation,
    selectedMap,
    editValueInfra,
    infrasEdited,
    setInfraestructures,
    iterationId,
    lastTimeReceived,
    eventsSended,
    isWaitingResponse, // Para manejar el estado de espera
    isPaused = true, // Por defecto, considerar que está pausado
  } = props;
  const organizationSettings = localStorage.getItem('organizationSettings');
  const mapStyle =
    JSON.parse(organizationSettings)?.mapSettings?.settings?.style ||
    'mapbox://styles/mapbox/light-v11';
  const { locationId, simulationId } = useParams();
  const subscriptionIS$ = interactiveSimulationsEventsService.getSubject();
  const [viewState, setViewState] = useState(initialViewState);
  // const [time, setTime] = useState(1);
  const [infrasLayer, setInfrasLayer] = useState([]);
  // const [animateLayer, setAnimateLayer] = useState([]);
  const [infras, setInfras] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  // const [infraInfo, setInfraInfo] = useState(null);
  const [selectedInfra, setSelectedInfra] = useState(null);
  const [popupCoordinates, setPopupCoordinates] = useState([]);
  const [animation] = useState({});
  const dispatch = useDispatch();
  const { value: context, setValue: setContext } = useContext(
    TourInteractiveSimulationContext,
  );
  const [temporalEventsToSend, setTemporalEventsToSend] = useState([]);

  const gisData = useGisMapData(Number(locationId) || null);
  const { layers: gisLayers } = useGisLayers({
    nodes: gisData.nodes,
    lines: gisData.lines,
    zoom: viewState.zoom,
    enabled: true,
  });

  useEffect(() => {
    subscriptionIS$.subscribe((event) => {
      if (event.type == 'clearTemporalEvents') {
        setTemporalEventsToSend([]);
      } else if (event.type === 'playSimulation') {
        // Limpiar eventos temporales cuando inicia la simulación
        setTemporalEventsToSend([]);
        setShowPopup(false); // Cerrar cualquier popup abierto
      }
    });
  }, []);

  useEffect(() => {
    if (selectedMap.latitude && selectedMap.longitude) {
      setViewState({
        longitude: selectedMap.longitude,
        latitude: selectedMap.latitude,
        zoom: selectedMap.zoomLevel,
        pitch: 0,
        bearing: 0,
      });
    }
  }, [selectedMap]);

  useEffect(() => {
    if (infras) {
      drawInfras();
    }
  }, [infras, eventsSended, temporalEventsToSend]);

  useEffect(() => {
    if (infras && infras.length > 0 && iterationId) {
      subscriptionIS$.subscribe((event) => {
        if (event.type == 'dataSocketRecived') {
          event.data.forEach((res) => {
            const infrasCopy = [...infras];
            const index = infrasCopy.findIndex((infra) => {
              return (
                infra.mainMeasurementTarget.measurementTargetId == res.paramId
              );
            });
            if (index >= 0) {
              infrasCopy[index].mainMeasurementTarget.currentValue = res.value;
              infrasCopy[index].mainMeasurementTarget.usage =
                (res.value /
                  infrasCopy[index].mainMeasurementTarget.criticalHhigh) *
                100;

              setInfras(infrasCopy);
            }
          });
        }
      });
    }
  }, [iterationId]);

  useEffect(() => {
    if (locationId) {
      dispatch(setSelectedLocationThunk(locationId));
      fetchData();
      loadMapInfo();
    }
  }, [locationId]);

  const getEditableInfras = useCallback(() => {
    return infras
      ? infras.filter((infra) => infra.editableAttributes == true)
      : [];
  }, [infras]);

  const loadMapInfo = async () => {
    await dispatch(getMapsAction(locationId));
    if (selectedMap.latitude && selectedMap.longitude) {
      setViewState({
        longitude: selectedMap.longitude,
        latitude: selectedMap.latitude,
        zoom: selectedMap.zoomLevel,
        pitch: 0,
        bearing: 0,
      });
    }
  };

  const fetchData = async () => {
    const infrasResult = await getInfraestructuresByLocation(locationId);
    if (infrasResult) {
      setInfras(infrasResult);
      setInfraestructures(infrasResult);
    }
  };

  const countModifiedTimes = (infra) => {
    let count = 0;
    eventsSended.forEach((ev) => {
      ev.events.forEach((es) => {
        if (es.infrastructureId == infra.measurementeInfraestructureId) {
          count++;
        }
      });
    });
    temporalEventsToSend.forEach((ev) => {
      if (ev.infrastructureId == infra.measurementeInfraestructureId) {
        count++;
      }
    });
    return count;
  };

  const infraIsEdited = (infra) => {
    return (
      temporalEventsToSend.findIndex(
        (ev) => ev.infrastructureId == infra.measurementeInfraestructureId,
      ) >= 0
    );
  };

  const drawInfras = async () => {
    if (!infras) return;

    try {
      // Generar los íconos de manera asíncrona
      const data = await Promise.all(
        infras.map(async (infra) => {
          const icon = await generateInfraImageToInteractiveSimulation(
            infra,
            countModifiedTimes(infra),
            infraIsEdited(infra),
          );
          return {
            ...infra,
            position: [parseFloat(infra.longitude), parseFloat(infra.latitude)],
            icon,
          };
        }),
      );

      // Crear la capa IconLayer con los datos procesados
      setInfrasLayer([
        new IconLayer({
          id: 'icon-layer',
          data,
          pickable: true,
          iconAtlas: null, // No es necesario un atlas si estás usando `getIcon`
          getPosition: (d) => d.position,
          getIcon: (d) => ({
            url: d.icon,
            width: 200,
            height: 200,
          }),
          sizeScale: 1,
          getSize: Math.max(10, 25 * (viewState.zoom / 10)),
          onHover: ({ object }) => {},
        }),
      ]);
    } catch (err) {
      console.error('Error al procesar las infraestructuras:', err);
    }
  };

  const handleClickMap = (event) => {
    // No permitir interacciones si la simulación está ejecutándose o esperando respuesta
    if (!isPaused || isWaitingResponse) {
      return;
    }

    // Los clicks sobre la red GIS no deben cerrar el popup de edición
    if (event.layer?.id?.startsWith('gis-')) {
      return;
    }

    if (event.picked) {
      if (
        event.object?.measurementeInfraestructureId &&
        event.object?.editableAttributes
      ) {
        setPopupCoordinates([event.object.longitude, event.object.latitude]);
        setShowPopup(true);
        setSelectedInfra(event.object);
        setContext({ ...context, activeStep: 4 });
        interactiveSimulationsEventsService.setSubject({
          type: "selectInfra",
        });
      }
    } else {
      setShowPopup(false);
      setSelectedInfra(null);
    }
  };

  function tooltipToshow(infra) {
    const element = (
      <>
      <div style={{ borderBottom: 'solid 4px rgb(7,140,179)' }}>
        <span style={{ textAlign: 'center' }}>
          {infra.measurementInfraestructureName}
        </span>
      </div>
      <div>
        {`${infra.mainMeasurementTarget?.currentValue} ${infra.mainMeasurementTarget?.unit}`}
      </div>
    </>
    );
    return renderToString(element);
  }

  const showPopUpDevice = (infra) => {
    // No mostrar popup si está en ejecución o esperando respuesta
    if (!isPaused || isWaitingResponse) {
      return;
    }
    setShowPopup(true);
    setSelectedInfra(infra);
    setPopupCoordinates([infra.longitude, infra.latitude]);
  };

  function getCurrentInputValueFromInfra(infra) {
    const eventsCopy = [...temporalEventsToSend];
    const indexEvent = eventsCopy.findIndex(
      (ev) => ev.infrastructureId == infra.measurementeInfraestructureId,
    );
    if (indexEvent >= 0) {
      return eventsCopy[indexEvent].mod_value;
    }
    return infra.mainMeasurementTarget.currentValue;
  }

  function sendedEventsList(selectedInfra) {
    const list = [];
    eventsSended.forEach((es) => {
      es.events.forEach((ev) => {
        if (
          ev.infrastructureId == selectedInfra.measurementeInfraestructureId
        ) {
          list.push({ ...ev, time: es.time });
        }
      });
    });
    return list;
  }

  useEffect(() => {
    interactiveSimulationsEventsService.setSubject({
      type: 'setEventsToSend',
      data: temporalEventsToSend,
    });
  }, [temporalEventsToSend]);

  return (
    <div
      className="MapContainerInteractiveSimulation"
      style={{ height: '100%', width: '100%', position: 'absolute' }}
    >
      <Map
        mapboxAccessToken={mapboxApiAccessToken}
        mapStyle={mapStyle}
        {...viewState}
      >
        <DeckGL
          controller
          viewState={viewState}
          layers={[...gisLayers, ...infrasLayer]}
          style={initialStyle}
          onViewStateChange={(nextViewState) => {
            setViewState(nextViewState.viewState);
          }}
          onClick={handleClickMap}
          getTooltip={(info) => {
            if (!info.object) return null;
            if (info.layer?.id?.startsWith('gis-')) {
              return getGisTooltip(info, viewState.zoom);
            }
            return {
              html: tooltipToshow(info.object),
              style: {
                backgroundColor: '#fff',
              },
            };
          }}
        >
          {lastTimeReceived && (
            <span
              style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
              }}
            >
              Last Time Received data:
              {dayjs(lastTimeReceived).format('DD/MM/YYYY HH:mm:ss')}
            </span>
          )}
          {showPopup && (
            <Popup
              longitude={popupCoordinates[0]}
              latitude={popupCoordinates[1]}
              style={{ width: '300px' }}
              maxWidth="300px"
              onClose={() => setShowPopup(false)}
            >
              <div style={{ width: '300px', padding: '10px' }}>
                <div
                  className="flex column"
                  style={{
                    width: 'auto',
                    padding: '10px 15px',
                    backgroundColor: '#F1F2F2',
                    borderRadius: '8px',
                  }}
                >
                  <span
                    className=""
                    style={{ color: '#023E65', fontWeight: '600' }}
                  >
                    {selectedInfra.measurementInfraestructureName}
                  </span>
                  <div
                    className="my-2"
                    style={{
                      width: '100%',
                      height: '1px',
                      backgroundColor: '#E6E6E6',
                    }}
                  />
                  <div className="grid grid-cols-2 gap-2 items-center">
                    <div className="flex flex-col">
                      <span>Setting</span>
                      <span>Current</span>
                    </div>
                    <InputNumber
                      placeholder={
                        selectedInfra.mainMeasurementTarget.currentValue
                      }
                      value={getCurrentInputValueFromInfra(selectedInfra)}
                      {...(() => {
                        const rules = getValidationRulesForInfraType(selectedInfra.infrastructureTypeId);
                        return {
                          min: rules.min,
                          max: rules.max,
                          step: rules.step,
                          precision: rules.precision
                        };
                      })()}
                      disabled={!isPaused || isWaitingResponse} // Deshabilitar si está en ejecución o esperando respuesta
                      onChange={(value) => {
                        // No permitir cambios si no está pausado o está esperando respuesta
                        if (!isPaused || isWaitingResponse) {
                          return;
                        }

                        // Validate value according to infrastructure type
                        const rules = getValidationRulesForInfraType(selectedInfra.infrastructureTypeId);
                        if (value !== null && value !== undefined && !rules.validateValue(value)) {
                          return;
                        }

                        setContext({
                          ...context,
                          activeStep: 6,
                        });
                        const eventsCopy = [...temporalEventsToSend];
                        const indexEvent = eventsCopy.findIndex(
                          (ev) =>
                            ev.infrastructureId ==
                            selectedInfra.measurementeInfraestructureId,
                        );
                        if (indexEvent >= 0) {
                          eventsCopy[indexEvent].mod_value = value;
                          setTemporalEventsToSend(eventsCopy);
                        } else {
                          eventsCopy.push({
                            infrastructureId:
                              selectedInfra.measurementeInfraestructureId,
                            infraName:
                              selectedInfra.measurementInfraestructureName,
                            mod_value: value,
                          });
                          setTemporalEventsToSend(eventsCopy);
                        }
                      }}
                    />
                  </div>
                  {sendedEventsList(selectedInfra).map((ev, key) => {
                    return (
                      <div
                        className="grid grid-cols-2 gap-2 items-center"
                        key={`evs-${key}`}
                      >
                        <div className="flex flex-col">
                          <span>
                            Setting
                          </span>
                          <span>At {dayjs(ev.time).format('hh:mm:ss A')}</span>
                        </div>
                        <InputNumber value={ev.mod_value} disabled />
                      </div>
                    );
                  })}
                </div>
              </div>
            </Popup>
          )}
          {context.showTour &&
            context.activeStep == 3 &&
            getEditableInfras().length > 0 && (
              <Popup
                longitude={parseFloat(getEditableInfras()[0].longitude)}
                latitude={parseFloat(getEditableInfras()[0].latitude)}
                anchor="bottom"
                style={{ width: '300px', backgroundColor: '#023E65' }}
                onClose={() => setShowPopup(false)}
              >
                <div
                  className="flex column"
                  style={{
                    backgroundColor: '#023E65',
                    borderRadius: '8px',
                    padding: '10px',
                  }}
                >
                  <div className="flex column text-white">
                    <span>
                      Then look for the infrastructure to edit and click on it.
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <Button
                      type="text"
                      style={{ color: 'white' }}
                      onClick={() => {
                        setContext({ ...context, activeStep: 2 });
                      }}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              </Popup>
            )}
        </DeckGL>
      </Map>
    </div>
  );
};

function mapState(state) {
  return {
    selectedLocation: state.locations.selectedLocation,
    selectedMap: state.maps.array,
  };
}

export default connect(mapState)(MapInteractiveSimulation);
